<template>
  <div>
    <!-- MenuItems -->
    <div
      class="grow-1"
      :class="[$vuetify.breakpoint.smAndUp ? 'mr-0' : 'mx-0']"
    >
      <v-layout
        align-center
        justify-space-between
        wrap
      >
        <menu-pages-navbar class="mb-3">
          <v-layout
            slot="extra"
            wrap
          >
            <v-spacer v-if="$vuetify.breakpoint.mdAndUp" />
            <!-- Search -->
            <v-text-field
              ref="search"
              v-model="searchText"
              :disabled="sortModeEnabled"
              :label="$t('search') + ' (' + searchHint + ')'"
              hide-details
              clearable
              append-icon="search"
              class="mt-0 pt-0"
              style="max-width: 240px"
              @click:clear="clearSearch()"
              @keyup.native.esc="clearSearch()"
            />
          </v-layout>
        </menu-pages-navbar>
      </v-layout>
      <!-- MenuCategoryDecoders Link -->
      <v-slide-x-transition>
        <div v-if="hasTVDecodersWithoutMenuCategories">
          <router-link
            :to="{
              name: 'menucategorydecoders',
              query: {
                without_categories: hasTVDecodersWithoutMenuCategories,
              },
            }"
          >
            <span
              style="word-break: break-word"
              class="warning--text"
            >{{
              $t('menuCategoryDecodersSomeMissingCategoriesHint')
            }}</span>
          </router-link>
        </div>
      </v-slide-x-transition>
      <!-- MenuCategory Tabs -->
      <div style="position: relative">
        <v-tabs
          v-if="orderedMenuCategories.length > 0 && !currentlySearching"
          :key="menuCategories.map((category) => category.id).join(',')"
          v-model="menuCategoryTabIndex"
          slider-color="msr"
          class="mt-4"
          style="width: calc(100% - 108px)"
          mandatory
        >
          <v-tab
            v-for="menuCategory in orderedMenuCategories"
            :key="'tab-' + menuCategory.id"
            class="v-tab--no-uppercase mr-1 subheading"
            :title="
              menuCategoryIdToAugmentedItems[menuCategory.id].length +
                ' ' +
                $t('menuItems')
            "
          >
            <!-- Deactivations Indicator -->
            <v-icon
              v-if="menuCategoryHasDeactivations(menuCategory)"
              :title="$t('menuCategoryHasItemsTemporarySoldOut')"
              color="error"
              class="mr-2"
              style="font-size: 9px"
            >
              circle
            </v-icon>
            <v-badge color="transparent">
              <!-- Timeframes Indicator -->
              <span
                v-if="
                  (menuCategoryIdToCategory[menuCategory.id].timeframes || [])
                    .length > 0
                "
                slot="badge"
              >
                <v-icon
                  style="font-size: 16px"
                  :color="
                    menuCategoryIdToCategory[menuCategory.id].active_now
                      ? 'success'
                      : 'error'
                  "
                  :title="
                    menuCategoryIdToCategory[menuCategory.id].active_now
                      ? $t('menuCategoryTimeframesActiveNow')
                      : $t('menuCategoryTimeframesInactiveNow')
                  "
                >
                  timelapse
                </v-icon>
              </span>
              <!-- Name -->
              {{ menuCategoryIdToCategory[menuCategory.id].name }}
            </v-badge>
          </v-tab>
        </v-tabs>
        <div
          v-if="!(menuCategories.length == 0 && loading) && !currentlySearching"
          style="position: absolute; top: 0; right: 0; min-width: 96px"
          class="v-tabs__div"
        >
          <!-- Go to specific tab dropdown -->
          <v-menu
            bottom
            left
          >
            <a
              slot="activator"
              slot-scope="{on}"
              class="v-tabs__div white unselectable"
              style="
                min-height: 48px;
                min-width: 48px;
                display: flex;
                justify-content: center;
              "
              :title="$t('goToMenuCategoryMenuItems')"
              v-on="on"
            >
              <v-icon
                large
                class="hoverable-icon"
              > arrow_drop_down </v-icon>
            </a>
            <v-list class="grey lighten-3">
              <v-list-tile
                v-for="menuCategory in orderedMenuCategories"
                :key="'go-to-tab-' + menuCategory.id"
                :class="{
                  'msr--text': menuCategory.id == selectedMenuCategory.id,
                }"
                @click="selectedMenuCategory = menuCategory"
              >
                {{ menuCategory.name }}
              </v-list-tile>
            </v-list>
          </v-menu>
          <!-- Create MenuCategory Button -->
          <v-btn
            v-if="!sortModeEnabled"
            dark
            style="border-radius: 0; min-width: 0; width: 48px; height: 48px"
            color="msr"
            class="unselectable ma-0 pa-4 elevation-0"
            :title="$t('menuCategoriesAddNew')"
            @click="showCreateMenuCategoryDialog = true"
          >
            <v-icon> add </v-icon>
          </v-btn>
        </div>
      </div>
      <div
        v-if="!loading && !atLeastOneMenuCategory"
        class="mt-3 warning--text subheading"
      >
        {{ $t('menuItemsPleaseCreateMenuCategoryFirst') }}.
      </div>
      <v-layout
        align-center
        wrap
        class="mt-3"
      >
        <!-- Buttons left -->
        <v-layout
          v-if="!sortModeEnabled && !currentlySearching"
          align-center
          wrap
        >
          <!-- Enable customize-order mode -->
          <v-btn
            class="ml-0"
            style="font-weight: normal; text-transform: none"
            small
            round
            flat
            :disabled="loading || selectedMenuCategoryMenuItems.length < 2"
            @click="sortModeEnabled = true"
          >
            <v-icon left>
              sort
            </v-icon>
            {{ $t('menuItemsReorder') }}
          </v-btn>
          <!-- MenuCategory additional info table -->
          <div
            v-if="
              showAdditionalInfoForSelectedMenuCategory &&
                !!selectedMenuCategory &&
                !currentlySearching &&
                !sortModeEnabled
            "
            class="transparent unselectable clickable mr-3"
            :title="$t('menuItemsSelectedMenuCategoryEdit')"
            @click.stop="
              $router.push({
                name: 'menucategories',
                params: {
                  menuId: menuId,
                },
                query: {
                  edit: selectedMenuCategory.id,
                },
              })
            "
          >
            <div
              style="display: flex; flex-flow: row wrap; align-items: center"
            >
              <!-- Details -->
              <v-layout
                v-if="selectedMenuCategory.details"
                align-center
                class="ml-3"
              >
                <v-icon
                  small
                  class="mr-2"
                  color="black"
                >
                  edit
                </v-icon>
                <span
                  class="pseudo-anchor--inverted pa-1"
                  style="
                    font-size: 13px;
                    font-style: italic;
                    display: block;
                    white-space: nowrap;
                    max-width: 240px;
                    max-height: 24px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  "
                >"{{ selectedMenuCategory.details }}"</span>
              </v-layout>
              <!-- Timeframes -->
              <div
                v-if="selectedMenuCategory.timeframes.length > 0"
                class="ml-3"
                :class="{'mt-2': $vuetify.breakpoint.xs}"
                style="max-width: 240px; overflow: hidden; white-space: nowrap"
              >
                <v-layout align-center>
                  <v-icon
                    :color="
                      selectedMenuCategory.active_now ? 'success' : 'error'
                    "
                    :title="
                      selectedMenuCategory.active_now
                        ? $t('menuCategoryTimeframesActiveNow')
                        : $t('menuCategoryTimeframesInactiveNow')
                    "
                    class="mr-2"
                    style="cursor: help; font-size: 1.33em; margin-bottom: 2px"
                  >
                    timelapse
                  </v-icon>
                  <entry-timeframe
                    :timeframes="selectedMenuCategory.timeframes || []"
                    :show-icon="false"
                    margin-class=""
                    horizontal
                  />
                </v-layout>
              </div>
            </div>
          </div>
          <!-- MenuCategory link to edit -->
          <v-btn
            v-if="!showAdditionalInfoForSelectedMenuCategory"
            style="font-weight: normal; text-transform: none"
            small
            round
            flat
            @click.stop="
              $router.push({
                name: 'menucategories',
                params: {
                  menuId: menuId,
                },
                query: {
                  edit: selectedMenuCategory.id,
                },
              })
            "
          >
            <v-icon
              left
              small
            >
              edit
            </v-icon>
            {{ $t('menuItemsSelectedMenuCategoryEdit') }}
          </v-btn>
        </v-layout>
        <!-- Buttons right -->
        <v-layout
          v-if="!currentlySearching"
          align-center
          justify-end
          wrap
          :class="{
            'mt-2': $vuetify.breakpoint.xs,
          }"
        >
          <!-- Buttons for sort mode -->
          <v-layout
            v-if="sortModeEnabled"
            align-center
            justify-start
            wrap
            class="mt-2"
          >
            <v-btn
              class="ml-0"
              round
              small
              flat
              @click="sortModeEnabled = false"
            >
              {{ $t('menuItemsReorderCancel') }}
            </v-btn>
            <v-btn
              class="ml-1"
              small
              round
              flat
              @click="sortMenuItemsByName()"
            >
              <v-icon
                class="mr-2"
                style="font-size: 20px"
              >
                sort_by_alpha
              </v-icon>
              {{ $t('menuItemsReorderByName') }}
            </v-btn>
            <v-btn
              round
              small
              color="msr"
              dark
              @click="confirmSortedMenuItems()"
            >
              {{ $t('menuItemsReorderConfirm') }}
            </v-btn>
          </v-layout>
          <!-- Preview Button -->
          <v-btn
            v-if="!sortModeEnabled"
            :disabled="
              !hasNormalMenuTemplates ||
                !menuCategories.length ||
                !menuItems.length
            "
            round
            color="info"
            :style="{
              'min-width': $vuetify.breakpoint.smAndUp ? '135px' : 'initial',
            }"
            :to="{name: 'menupreview', params: {menuId: menuId}}"
          >
            {{ $t('preview') }}
          </v-btn>
          <!-- Create MenuItem Button -->
          <v-btn
            v-if="!sortModeEnabled"
            :disabled="!atLeastOneMenuCategory"
            color="msr"
            round
            :dark="atLeastOneMenuCategory"
            class="mr-0"
            data-cy="add-menuitem-btn"
            :to="{
              name: 'createmenuitem',
              params: {menuId: menuId},
              query: {category: (selectedMenuCategory || {}).id},
            }"
          >
            <v-icon left>
              add
            </v-icon>
            {{ $t('menuItem') }}
          </v-btn>
        </v-layout>
      </v-layout>
      <!-- Loading Indicator -->
      <div
        v-if="loading && menuItems.length == 0"
        class="loading-indicator mt-5"
      >
        <v-progress-circular
          :size="50"
          indeterminate
          class="msr--text"
        />
      </div>
      <!-- MenuItems table for selected MenuCategory -->
      <div v-if="!sortModeEnabled">
        <!-- Search Results -->
        <menu-items-search-results
          v-if="currentlySearching"
          class="mt-4 white pt-2 pb-1"
          :class="{'px-5': $vuetify.breakpoint.lgAndUp}"
          :menu-items="filteredAugmentedMenuItems"
          :menu-id="menuId"
          @search-result-selected="onMenuItemsSearchResultSelected"
          @clear-search="clearSearch()"
        />
        <!-- MenuItems Table -->
        <v-slide-y-transition>
          <v-data-table
            v-if="
              selectedMenuCategory &&
                !currentlySearching &&
                menuItems.length > 0
            "
            hide-actions
            disable-initial-sort
            :headers="menuItemHeaders"
            :items="selectedMenuCategoryMenuItems"
            :loading="loadingItems"
            :no-data-text="
              loadingItems ? '' : $t('menuCategoryHasNoMenuItemsYet')
            "
            class="mt-3 menu-items-table"
          >
            <template
              slot="items"
              slot-scope="props"
            >
              <tr
                :key="props.item.id"
                class="menu-item-row"
                :data-menuitemid="props.item.id"
                :class="{
                  'menu-item--temporary-highlight':
                    props.item.has_just_been_created &&
                    !(!!menu && menu.has_just_been_created),
                  'menu-item--is-disabled': !props.item.is_enabled,
                }"
              >
                <!-- Name && Deactivation Status -->
                <td
                  class="unselectable clickable menu-item-name-column"
                  :title="$t('editmenuitem')"
                  @click="editMenuItem(props.item)"
                >
                  <v-layout
                    align-center
                    class="body-1"
                    style="min-width: 120px"
                  >
                    <!-- Deactivation Status -->
                    <span
                      v-if="selectedMenuCategoryHasDeactivations"
                      :title="$t('editmenuitem')"
                      class="clickable"
                      style="width: 5%; display: inline-table"
                      @click="editMenuItem(props.item)"
                    >
                      <div
                        v-if="!props.item.has_deactivations"
                        style="min-width: 36px"
                      />
                      <v-menu
                        v-else
                        :ref="'deactivations-menu-' + props.item.id"
                        offset-y
                      >
                        <v-btn
                          slot="activator"
                          icon
                          small
                          class="ml-0 my-0"
                          :title="$t('menuItemTemporarySoldOutTooltip')"
                          @click.stop="
                            onDeactivationsMenuActivatorClicked(props.item)
                          "
                        >
                          <v-icon
                            color="error"
                            style="font-size: 12px"
                          >
                            circle
                          </v-icon>
                        </v-btn>
                        <v-list>
                          <v-list-tile class="unselectable">
                            {{
                              $t('menuItemTemporarySoldOutMenuHeader', {
                                product: props.item.name,
                              })
                            }}:
                          </v-list-tile>
                          <v-divider />
                          <v-list-tile
                            v-for="deactivation in props.item.deactivations"
                            :key="deactivation.id"
                            :title="$t('menuTVDecoderStoreMenuLink')"
                            @click="
                              goToStoreWithExpandedMenuItems(deactivation.store)
                            "
                          >
                            <v-layout
                              class="body-1"
                              align-center
                            >
                              <v-icon
                                medium
                                class="msr--text mr-3 unselectable"
                              >
                                location_on
                              </v-icon>
                              <div>
                                <small
                                  class="d-block grey--text text--darken-2"
                                >{{ deactivation.store.name }}</small>
                                <strong>{{ deactivation.store.city }}</strong>
                              </div>
                            </v-layout>
                          </v-list-tile>
                        </v-list>
                      </v-menu>
                    </span>
                    <!-- Name -->
                    <span
                      :class="{
                        'menu-item--name--line-through':
                          !props.item.is_enabled &&
                          !!menu &&
                          menu.show_menu_item_when_disabled,
                      }"
                    >{{ props.item.name }}</span>
                  </v-layout>
                </td>
                <!-- Highlight -->
                <td
                  v-if="hasHighlightMenuTemplates"
                  style="width: 5%"
                  class="clickable"
                  @click="openHighlightMenuItemDialog(props.item)"
                >
                  <v-btn
                    class="match-row-opacity ma-0 mr-1"
                    :class="{
                      'grey--text text--lighten-1':
                        !props.item.is_enabled || !props.item.is_highlight,
                      'msr--text':
                        props.item.is_enabled && props.item.is_highlight,
                    }"
                    icon
                    flat
                    :disabled="loadingItems"
                    :title="
                      props.item.is_highlight
                        ? $t('menuItemIsHighlightTooltip')
                        : $t('menuItemIsNotHighlightTooltip')
                    "
                  >
                    <img
                      src="/static/icon_krone_01.svg"
                      width="24"
                      height="24"
                      class="svg-hoverable-icon"
                      :class="[
                        props.item.is_highlight
                          ? 'filter-to-msr-color-primary'
                          : '',
                      ]"
                      :style="{opacity: props.item.is_highlight ? 1.0 : 0.75}"
                    >
                  </v-btn>
                </td>
                <!-- Image -->
                <td
                  v-if="selectedMenuCategoryHasMenuItemsWithImage"
                  class="clickable unselectable"
                  :title="$t('editmenuitem')"
                  @click="editMenuItem(props.item)"
                >
                  <v-img
                    v-if="props.item.image"
                    :src="props.item.image"
                    :aspect-ratio="menuItemImageAspectRatio"
                    cover
                    :min-height="menuItemImageSize"
                    :min-width="menuItemImageSize"
                    :max-height="menuItemImageSize"
                    :max-width="menuItemImageSize"
                    style="margin-top: 4px; margin-bottom: 4px"
                  />
                </td>
                <!-- Has Beverage Deposit -->
                <td
                  v-if="selectedMenuCategoryHasMenuItemsWithBeverageDeposit"
                  class="clickable unselectable"
                  :title="$t('editmenuitem')"
                  @click="editMenuItem(props.item)"
                >
                  <div
                    v-if="
                      props.item.beverage_deposit &&
                        idToMenuBeverageDeposit[props.item.beverage_deposit]
                    "
                    class="unselectable"
                    style="text-transform: uppercase"
                  >
                    {{
                      idToMenuBeverageDeposit[props.item.beverage_deposit].value
                        | price
                    }}
                    €
                  </div>
                </td>
                <!-- Is new -->
                <td
                  v-if="selectedMenuCategoryHasMenuItemsWithIsNew"
                  class="clickable unselectable"
                  :title="$t('editmenuitem')"
                  @click="editMenuItem(props.item)"
                >
                  <div
                    v-if="props.item.is_new"
                    class="body-2 msr--text unselectable"
                    style="text-transform: uppercase"
                  >
                    {{ $t('menuItemIsNew') }}
                  </div>
                </td>
                <!-- Veggie & Co -->
                <td
                  v-if="selectedMenuCategoryHasMenuItemsWithDietaryChoices"
                  class="clickable unselectable nowrap"
                  :title="$t('editmenuitem')"
                  @click="editMenuItem(props.item)"
                >
                  <span>
                    <!-- Vegetarian or Vegan -->
                    <span
                      v-if="props.item.is_vegan"
                      class="unselectable mr-1"
                      :title="$t('menuItemIsVegan')"
                    >
                      <img
                        :src="symbolVeganUrl"
                        width="20"
                      >
                    </span>
                    <span
                      v-else-if="props.item.is_vegetarian"
                      class="unselectable mr-1"
                      :title="$t('menuItemIsVegetarian')"
                    >
                      <img
                        :src="symbolVegetarianUrl"
                        width="20"
                      >
                    </span>
                    <!-- Gluten free, Lactose free -->
                    <span
                      v-if="props.item.is_gluten_free"
                      class="unselectable mr-1"
                      :title="$t('menuItemIsGlutenFree')"
                    >
                      <img
                        :src="symbolGlutenFreeUrl"
                        width="20"
                      >
                    </span>
                    <span
                      v-if="props.item.is_lactose_free"
                      class="unselectable"
                      :title="$t('menuItemIsLactoseFree')"
                    >
                      <img
                        :src="symbolLactoseFreeUrl"
                        width="20"
                      >
                    </span>
                  </span>
                </td>
                <!-- Description via MenuItem and MenuItemVariants -->
                <td
                  class="clickable unselectable"
                  :title="$t('editmenuitem')"
                  align="left"
                  @click="editMenuItem(props.item)"
                >
                  <div
                    v-if="
                      props.item.description || props.item.additives.length > 0
                    "
                    class="nowrap mt-1"
                    style="
                      display: block;
                      max-width: 240px;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    "
                    :title="props.item.description"
                  >
                    {{ props.item.description || '&nbsp;' }}
                  </div>
                  <div
                    v-if="shouldDisplayVariantFieldsInMenuItemRow(props.item)"
                  >
                    <div
                      v-for="(variant, variantIndex) of props.item.variants"
                      :key="variantIndex"
                      class="nowrap grey--text text--darken-3"
                      :class="{
                        'mb-1': variantIndex == props.item.variants.length - 1,
                      }"
                      style="
                        display: block;
                        max-width: 240px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        font-weight: lighter;
                      "
                      :title="variant.description"
                    >
                      {{ variant.description || '&nbsp;' }}
                    </div>
                  </div>
                </td>
                <!-- Additives via MenuItem and MenuItemVariants -->
                <td
                  class="nowrap clickable unselectable"
                  :title="
                    additivesTooltipCache[
                      props.item.additives.map((a) => a.index).join(',')
                    ]
                  "
                  @click="editMenuItem(props.item)"
                >
                  <div
                    v-if="
                      props.item.description || props.item.additives.length > 0
                    "
                    class="mt-1"
                  >
                    {{
                      props.item.additives | formatAdditiveIndicesAsCSV
                    }}&nbsp;
                  </div>
                  <div
                    v-if="shouldDisplayVariantFieldsInMenuItemRow(props.item)"
                  >
                    <div
                      v-for="(variant, variantIndex) of props.item.variants"
                      :key="variantIndex"
                      class="grey--text text--darken-3"
                      style="font-weight: lighter"
                      :class="{
                        'mb-1': variantIndex == props.item.variants.length - 1,
                      }"
                    >
                      {{ variant.additives | formatAdditiveIndicesAsCSV }}&nbsp;
                    </div>
                  </div>
                </td>
                <!-- Amount via MenuItemVariants -->
                <td
                  class="
                    nowrap
                    clickable
                    unselectable
                    grey--text
                    text--darken-1
                  "
                  align="right"
                  :title="$t('editmenuitem')"
                  @click="editMenuItem(props.item)"
                >
                  <div
                    v-if="
                      props.item.variants.length > 1 ||
                        shouldDisplayVariantFieldsInMenuItemRow(props.item)
                    "
                    class="mt-1"
                  >
                    <!-- Reserved space for tabular alignment: -->&nbsp;
                  </div>
                  <div
                    v-for="(variant, variantIndex) of props.item.variants"
                    :key="variantIndex"
                    :class="{
                      'mt-2':
                        props.item.variants.length == 1 &&
                        !shouldDisplayVariantFieldsInMenuItemRow(props.item),
                      'mb-1': variantIndex == props.item.variants.length - 1,
                    }"
                    style="
                      max-width: 120px;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    "
                  >
                    {{ variant.amount || '' }}&nbsp;
                  </div>
                </td>
                <!-- Price via MenuItemVariants -->
                <td
                  class="nowrap clickable unselectable"
                  :title="$t('editmenuitem')"
                  @click="editMenuItem(props.item)"
                >
                  <div
                    v-if="
                      props.item.variants.length > 1 ||
                        shouldDisplayVariantFieldsInMenuItemRow(props.item)
                    "
                    class="mt-1"
                  >
                    <!-- Reserved space for tabular alignment: -->&nbsp;
                  </div>
                  <div
                    v-for="(variant, variantIndex) of props.item.variants"
                    :key="variantIndex"
                    class="text-xs-right"
                    :class="{
                      'mt-2':
                        props.item.variants.length == 1 &&
                        !shouldDisplayVariantFieldsInMenuItemRow(props.item),
                      'mb-1': variantIndex == props.item.variants.length - 1,
                    }"
                  >
                    <span
                      style="
                        display: inline-block;
                        min-width: 8px;
                        max-width: 8px;
                        text-align: center;
                      "
                      :style="{
                        opacity: variant.modifier == '=' ? 0 : 1,
                        transform: variant.modifier == '-' ? 'scaleX(1.5)' : '',
                      }"
                    >{{ variant.modifier }}</span>
                    {{ variant.price }} €
                  </div>
                </td>
                <!-- Hard Range -->
                <td
                  v-if="selectedMenuCategoryHasMenuItemsWithHardRange"
                  class="clickable unselectable"
                  :title="$t('editmenuitem')"
                  @click="editMenuItem(props.item)"
                >
                  <span
                    class="nowrap"
                    :class="{
                      'success--text': props.item.active_now,
                      'warning--text': props.item.planned_now,
                      'error--text':
                        !props.item.active_now && !props.item.planned_now,
                    }"
                  >{{ props.item.hard_range }}</span>
                </td>
                <td
                  style="width: 32px; padding-right: 0"
                  class="buttons-column"
                >
                  <v-layout align-center>
                    <v-menu>
                      <v-btn
                        slot="activator"
                        icon
                        class="ma-0 grey--text text--lighten-1"
                      >
                        <v-icon
                          medium
                          class="hoverable-icon"
                        >
                          more_vert
                        </v-icon>
                      </v-btn>
                      <v-list>
                        <!-- Enable/Disable -->
                        <v-list-tile
                          :disabled="loadingItems"
                          :title="
                            props.item.is_enabled
                              ? !!menu && menu.show_menu_item_when_disabled
                                ? $t('menuItemIsEnabledAlternativeTooltip')
                                : $t('menuItemIsEnabledTooltip')
                              : $t('menuItemIsDisabledTooltip')
                          "
                          @click="confirmToggleMenuItemIsEnabled(props.item)"
                        >
                          <v-list-tile-avatar>
                            <v-icon
                              v-show="!props.item.is_enabled"
                              color="msr"
                            >
                              visibility_off
                            </v-icon>
                            <v-icon v-show="props.item.is_enabled">
                              visibility
                            </v-icon>
                          </v-list-tile-avatar>
                          {{
                            props.item.is_enabled
                              ? $t('menuItemIsEnabledActionLabel')
                              : $t('menuItemIsDisabledActionLabel')
                          }}
                        </v-list-tile>
                        <!-- Copy/Duplicate -->
                        <v-list-tile
                          :disabled="loadingItems"
                          :title="$t('duplicate')"
                          @click="openDuplicateMenuItemDialog(props.item)"
                        >
                          <v-list-tile-avatar>
                            <v-icon>content_copy</v-icon>
                          </v-list-tile-avatar>
                          {{ $t('duplicate') }}
                        </v-list-tile>
                        <!-- Delete -->
                        <v-list-tile
                          :disabled="loadingItems"
                          :title="$t('delete')"
                          @click="confirmDeleteMenuItem(props.item)"
                        >
                          <v-list-tile-avatar>
                            <v-icon>delete</v-icon>
                          </v-list-tile-avatar>
                          {{ $t('delete') }}
                        </v-list-tile>
                      </v-list>
                    </v-menu>
                  </v-layout>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-slide-y-transition>
      </div>
      <!-- 'Sort mode' MenuItems list -->
      <div
        v-else
        class="mt-4"
      >
        <div class="unselectable grey--text text--darken-3 mb-2">
          {{ $t('menuItemsReorderHint') }}
        </div>
        <div class="full-width">
          <sortable-menu-items
            v-model="sortableMenuItems"
            class="white"
          />
        </div>
      </div>
    </div>
    <!-- Scroll Spacer -->
    <div style="min-height: 60px" />
    <!-- Mandatory dialog with how-to stepper/wizard on first visit -->
    <menu-how-to-stepper-dialog :show.sync="showMenuHowToStepperDialog" />
    <!-- Create MenuCategory Dialog -->
    <create-edit-menu-category-dialog
      :show.sync="showCreateMenuCategoryDialog"
      :show-menu-category-details="(menu || {}).show_menu_category_details"
      :show-menu-category-timeframes="
        (menu || {}).show_menu_category_timeframes
      "
      :show-menu-category-backgrounds="
        (menu || {}).show_menu_category_backgrounds
      "
      :menu-id="menuId"
      :menu-categories="menuCategories"
      show-categories-link
      @save="(name) => createMenuCategory(name)"
    />
    <!-- Duplicate MenuItem Dialog -->
    <duplicate-menu-item-dialog
      v-if="menuItemToDuplicate"
      :show.sync="showDuplicateMenuItemDialog"
      :menu-item-to-duplicate="menuItemToDuplicate"
      :menu-categories="menuCategories"
      :menu-items="menuItems"
      @save="(payload) => duplicateMenuItem(payload)"
    />
    <!-- MenuItem Highlight Dialog -->
    <highlight-menu-item-dialog
      v-if="menuItemToHighlight"
      :show.sync="showHighlightMenuItemDialog"
      :menu-item="menuItemToHighlight"
      :menu-id="menuId"
      :busy="busyUpdatingMenuItem"
      @change="
        (isHighlight) =>
          toggleMenuItemIsHighlighted(menuItemToHighlight, isHighlight)
      "
    />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import orderBy from 'lodash.orderby';
import {mapState} from 'vuex';

import {removeUrlQueryParamsFromCurrentRoute} from '@/utils';
import matchesFilter from '@/matchesFilter';

import menuBeverageDeposits from '@/mixins/menuBeverageDeposits.js';

import CreateEditMenuCategoryDialog from '@/components/Menu/CreateEditMenuCategoryDialog';
import DuplicateMenuItemDialog from '@/components/Menu/DuplicateMenuItemDialog';
import EntryTimeframe from '@/components/EntryTimeframe';
import HighlightMenuItemDialog from '@/components/Menu/HighlightMenuItemDialog';
import MenuHowToStepperDialog from '@/components/Menu/MenuHowToStepperDialog';
import MenuItemsSearchResults from '@/components/Menu/MenuItemsSearchResults';
import MenuPagesNavbar from '@/components/Menu/MenuPagesNavbar';
import SortableMenuItems from '@/components/Menu/SortableMenuItems';

export const SYMBOL_VEGAN_URL =
  'https://cms.myshopradio.de/filer/canonical/1710249283/1126/';
export const SYMBOL_VEGETARIAN_URL =
  'https://cms.myshopradio.de/filer/canonical/1710249285/1128/';
export const SYMBOL_GLUTEN_FREE_URL =
  'https://cms.myshopradio.de/filer/canonical/1710249283/1127/';
export const SYMBOL_LACTOSE_FREE_URL =
  'https://cms.myshopradio.de/filer/canonical/1710249283/1125/';

// We decided this is not helpful when we can order MenuItems ourself on
// top, as the user might get confused what the actual sorting order is.
const COLUMN_SORTING_ENABLED = false;

const MENUS_VISITED_BEFORE_KEY = 'menus:visited';

export function hasVisitedMenusBefore() {
  return !!localStorage.getItem(MENUS_VISITED_BEFORE_KEY);
}

export default {
  components: {
    CreateEditMenuCategoryDialog,
    DuplicateMenuItemDialog,
    EntryTimeframe,
    HighlightMenuItemDialog,
    MenuHowToStepperDialog,
    MenuItemsSearchResults,
    MenuPagesNavbar,
    SortableMenuItems,
  },
  filters: {
    formatAdditiveIndicesAsCSV(additives) {
      return (
        additives
          .map((a) => a.index)
          // Numerical sort: https://stackoverflow.com/a/1063027
          .sort((a, b) => a - b)
          .join(',')
      );
    },
  },
  mixins: [menuBeverageDeposits],
  data() {
    return {
      additivesTooltipCache: {},

      loadingMenu: false,
      loadingCategories: false,
      loadingItems: false,
      busyUpdatingMenuItem: false,

      editedMenuItem: null,
      menuItemToDuplicate: null,
      menuItemToHighlight: null,

      menuCategoryTabIndex: 0,

      sortModeEnabled: false,
      sortableMenuItems: [],

      showMenuHowToStepperDialog: false,
      showCreateMenuCategoryDialog: false,
      showDuplicateMenuItemDialog: false,
      showHighlightMenuItemDialog: false,

      symbolVeganUrl: SYMBOL_VEGAN_URL,
      symbolVegetarianUrl: SYMBOL_VEGETARIAN_URL,
      symbolGlutenFreeUrl: SYMBOL_GLUTEN_FREE_URL,
      symbolLactoseFreeUrl: SYMBOL_LACTOSE_FREE_URL,

      // From backend.
      menu: null,
      hasTVDecodersWithoutMenuCategories: false,
    };
  },
  computed: {
    ...mapState({
      menuAdditives: (state) => state.common.menuAdditives,
      menuCategories: (state) => state.common.menuCategories,
      menuItems: (state) => state.common.menuItems,
      menuItems: (state) => state.common.menuItems,
      lastSelectedMenuCategory: (state) =>
        state.common.lastSelectedMenuCategory,
    }),
    menuItemImageSize() {
      if (this.$store.state.common.fullscreenMode) {
        return 64;
      }
      return 48;
    },
    menuItemImageAspectRatio() {
      if (
        this.menu &&
        this.menu.menu_item_image_width &&
        this.menu.menu_item_image_height
      ) {
        return (
          this.menu.menu_item_image_width / this.menu.menu_item_image_height
        );
      }
      return 1;
    },
    showAdditionalInfoForSelectedMenuCategory() {
      return (
        !!this.selectedMenuCategory &&
        (!!this.selectedMenuCategory.details ||
          this.selectedMenuCategory.timeframes.length > 0)
      );
    },
    currentlySearching() {
      return !!this.searchText;
    },
    searchText: {
      get() {
        return this.$store.state.common.menuItemsSearchText;
      },
      set(searchText) {
        this.$store.commit('SET_MENU_ITEMS_SEARCH_TEXT', searchText);
      },
    },
    menuTemplates() {
      if (!this.menu) {
        return [];
      }
      return this.menu.menu_templates;
    },
    normalMenuTemplates() {
      return this.menuTemplates.filter(
        (template) => !template.is_highlight_clip_template
      );
    },
    hasNormalMenuTemplates() {
      return this.normalMenuTemplates.length > 0;
    },
    highlightMenuTemplates() {
      return this.menuTemplates.filter(
        (template) => template.is_highlight_clip_template
      );
    },
    hasHighlightMenuTemplates() {
      return this.highlightMenuTemplates.length > 0;
    },
    menuItemHeaders() {
      const headers = [
        {
          text: this.$t('name'),
          value: 'name',
          sortable: COLUMN_SORTING_ENABLED,
          align: 'left',
        },
        this.hasHighlightMenuTemplates
          ? {
              text: this.$t('menuItemHighlightColumnHeader'),
              value: 'is_highlight',
              sortable: COLUMN_SORTING_ENABLED,
              align: 'left',
            }
          : null,
        this.selectedMenuCategoryHasMenuItemsWithImage
          ? {
              text: this.$t('menuItemImage'),
              value: 'image',
              sortable: COLUMN_SORTING_ENABLED,
              width: '96px',
              align: 'left',
            }
          : null,
        this.selectedMenuCategoryHasMenuItemsWithBeverageDeposit
          ? {
              text: this.$t('menuItemBeverageDepositShort'),
              value: 'beverage_deposit',
              sortable: COLUMN_SORTING_ENABLED,
            }
          : null,
        this.selectedMenuCategoryHasMenuItemsWithIsNew
          ? {
              text: this.$t('menuItemIsNew'),
              value: 'is_new',
              sortable: COLUMN_SORTING_ENABLED,
              align: 'left',
            }
          : null,
        this.selectedMenuCategoryHasMenuItemsWithDietaryChoices
          ? {
              text: this.$t('menuItemIsVegetarianEtc'),
              value: 'is_vegetarian',
              sortable: false,
              align: 'left',
            }
          : null,
        {
          text: this.$t('description'),
          value: 'variants',
          sortable: false,
          align: 'left',
        },
        {
          text: this.$t('menuAdditives'),
          value: 'variants',
          sortable: false,
          width: '72px',
        },
        {
          text: this.$t('menuItemAmount'),
          value: 'variants',
          sortable: false,
          align: 'right',
          width: '120px',
        },
        {
          text: this.$t('price'),
          value: 'variants',
          sortable: false,
          align: 'right',
          width: '72px',
        },
        this.selectedMenuCategoryHasMenuItemsWithHardRange
          ? {
              text: this.$t('menuItemHardRange'),
              value: 'hard_range',
              sortable: COLUMN_SORTING_ENABLED,
            }
          : null,
      ];
      return headers.filter((header) => header != null);
    },
    selectedMenuCategoryMenuItems() {
      if (!this.selectedMenuCategory) {
        return [];
      }
      return this.menuCategoryIdToAugmentedItems[this.selectedMenuCategory.id];
    },
    selectedMenuCategoryHasDeactivations() {
      return this.menuCategoryHasDeactivations(this.selectedMenuCategory);
    },
    selectedMenuCategoryHasMenuItemsWithImage() {
      return (
        this.selectedMenuCategoryMenuItems.filter((item) => !!item.image)
          .length > 0
      );
    },
    selectedMenuCategoryHasMenuItemsWithBeverageDeposit() {
      return (
        this.selectedMenuCategoryMenuItems.filter(
          (item) => item.beverage_deposit !== null
        ).length > 0
      );
    },
    selectedMenuCategoryHasMenuItemsWithHardRange() {
      return (
        this.selectedMenuCategoryMenuItems.filter(
          (item) => !!item.hard_start || !!item.hard_expire
        ).length > 0
      );
    },
    selectedMenuCategoryHasMenuItemsWithIsNew() {
      return (
        this.selectedMenuCategoryMenuItems.filter((item) => !!item.is_new)
          .length > 0
      );
    },
    selectedMenuCategoryHasMenuItemsWithDietaryChoices() {
      return (
        this.selectedMenuCategoryMenuItems.filter(
          (item) =>
            item.is_vegetarian ||
            item.is_vegan ||
            item.is_gluten_free ||
            item.is_lactose_free
        ).length > 0
      );
    },
    orderedMenuCategories() {
      return orderBy(this.menuCategories, ['order', 'name']);
    },
    selectedMenuCategory: {
      get() {
        return this.orderedMenuCategories[this.menuCategoryTabIndex];
      },
      set(menuCategory) {
        for (let i = 0; i < this.orderedMenuCategories.length; i++) {
          const category = this.orderedMenuCategories[i];
          if (category.id == menuCategory.id) {
            this.menuCategoryTabIndex = i;
            break;
          }
        }
      },
    },
    menuId() {
      return this.$route.params.menuId;
    },
    loading() {
      return this.loadingMenu || this.loadingCategories || this.loadingItems;
    },
    atLeastOneMenuCategory() {
      return this.menuCategories.length > 0;
    },
    searchHint() {
      if (!this.currentlySearching) {
        return `${this.menuItems.length} ${this.$t('menuItems')}`;
      }
      return `${this.filteredAugmentedMenuItems.length}/${
        this.menuItems.length
      } ${this.$t('menuItems')}`;
    },
    augmentedMenuItems() {
      const menuItems = this.menuItems.slice();
      menuItems.forEach((item) => {
        // Neither hard_start nor hard_expire.
        item.hard_range = '';

        // We can omit the year if it's the current one and both dates
        // are in the same year. Write it out in fullscreen mode,
        // though.
        let formatString = 'DD.MM.YYYY';

        // Only hard_start.
        if (item.hard_start && !item.hard_expire) {
          if (
            !this.$store.state.common.fullscreenMode &&
            new dayjs(item.hard_start).year() == new dayjs().year()
          ) {
            formatString = 'DD.MM.';
          }

          item.hard_range =
            this.$t('hardStart') +
            ' ' +
            new dayjs(item.hard_start).format(formatString);
        }
        // Both hard_start and hard_expire
        else if (item.hard_start && item.hard_expire) {
          if (
            !this.$store.state.common.fullscreenMode &&
            new dayjs(item.hard_start).year() == new dayjs().year() &&
            new dayjs(item.hard_start).year() ==
              new dayjs(item.hard_expire).year()
          ) {
            formatString = 'DD.MM.';
          }

          item.hard_range =
            new dayjs(item.hard_start).format(formatString) +
            ' - ' +
            new dayjs(item.hard_expire).format(formatString);
        }
        // Only hard_expire.
        else if (!item.hard_start && item.hard_expire) {
          if (
            !this.$store.state.common.fullscreenMode &&
            new dayjs(item.hard_expire).year() == new dayjs().year()
          ) {
            formatString = 'DD.MM.';
          }

          item.hard_range =
            this.$t('hardExpire') +
            ' ' +
            new dayjs(item.hard_expire).format(formatString);
        }
      });
      return menuItems;
    },
    filteredAugmentedMenuItems() {
      if (!this.currentlySearching) {
        return this.augmentedMenuItems;
      }
      return this.augmentedMenuItems.filter((menuItem) => {
        return (
          matchesFilter(menuItem.category.name, this.searchText) ||
          matchesFilter(menuItem.name, this.searchText) ||
          matchesFilter(menuItem.description, this.searchText)
        );
      });
    },
    menuCategoryIdToCategory() {
      const idToCategory = {};
      for (const menuCategory of this.menuCategories) {
        idToCategory[menuCategory.id] = menuCategory;
      }
      return idToCategory;
    },
    menuCategoryIdToAugmentedItems() {
      const categoryIdToItems = {};

      // Ensure empty categories are also included.
      for (const menuCategory of this.menuCategories) {
        categoryIdToItems[menuCategory.id] = [];
      }

      for (const menuItem of this.augmentedMenuItems) {
        const items = categoryIdToItems[menuItem.category.id] || [];
        items.push(menuItem);
        categoryIdToItems[menuItem.category.id] = items;
      }
      return categoryIdToItems;
    },
  },
  watch: {
    menuCategoryTabIndex: function () {
      this.$store.commit(
        'SET_LAST_SELECTED_MENU_CATEGORY',
        this.selectedMenuCategory
      );
      this.updateSortableMenuItems();
    },
    sortModeEnabled: function () {
      this.updateSortableMenuItems();
    },
  },
  created() {
    this.fetchData();
  },
  mounted() {
    this.setupPageHotkeyOverrides();
    this.maybeShowMenuHowToStepperDialog();
  },
  beforeDestroy() {
    this.removePageHotkeyOverrides();
  },
  methods: {
    maybeShowMenuHowToStepperDialog() {
      if (!hasVisitedMenusBefore()) {
        this.showMenuHowToStepperDialog = true;
        localStorage.setItem(
          MENUS_VISITED_BEFORE_KEY,
          new Date().toISOString()
        );
      }
    },
    shouldDisplayVariantFieldsInMenuItemRow(menuItem) {
      return (
        menuItem.variants.filter(
          (v) => !!v.description || !!v.amount || !!v.additives.length
        ).length > 0
      );
    },
    /**
     * Map concatenated indices to a human-readable tooltip, e.g.
     * '2,13' -> 'Konservierungsmittel (2), Koffeinhaltig (13)'
     */
    populateAdditivesTooltipCache() {
      for (const menuItem of this.menuItems) {
        const additives = menuItem.additives;

        const sortedByIndex = [...additives].sort((a, b) => a.index - b.index);
        const tooltipKey = sortedByIndex
          .map((additive) => additive.index)
          .join(',');

        let tooltip = this.additivesTooltipCache[tooltipKey];
        if (!tooltip) {
          tooltip = sortedByIndex
            .map((additive) => additive.name + ' (' + additive.index + ')')
            .join(', ');
          this.additivesTooltipCache[tooltipKey] = tooltip;
        }
      }
    },
    setupPageHotkeyOverrides() {
      window.addEventListener('keydown', this.onPageKeydown);
    },
    removePageHotkeyOverrides() {
      window.removeEventListener('keydown', this.onPageKeydown);
    },
    /**
     * Temporarily override some hotkeys for search etc.
     */
    onPageKeydown(event) {
      // Ctrl + F: Focus our search field instead of using browser search.
      if (event.ctrlKey && event.key == 'f') {
        event.preventDefault();
        const search = this.$refs.search;
        if (search) {
          search.focus();
          // focus() will jump if we scrolled far away, but do nothing
          // if the search field is only visible partially, so we do
          // this on top.
          search.$el.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
      }
      // Left/right arrow: Select previous/next MenuCategory Tab
      else if (event.key == 'ArrowLeft') {
        this.menuCategoryTabIndex = this.menuCategoryTabIndex - 1;
        if (this.menuCategoryTabIndex < 0) {
          this.menuCategoryTabIndex = this.menuCategories.length - 1;
        }
      } else if (event.key == 'ArrowRight') {
        this.menuCategoryTabIndex = this.menuCategoryTabIndex + 1;
        if (this.menuCategoryTabIndex > this.menuCategories.length - 1) {
          this.menuCategoryTabIndex = 0;
        }
      }
    },
    onMenuItemsSearchResultSelected(menuItem) {
      this.clearSearch();
      this.selectedMenuCategory = menuItem.category;

      // Scroll to MenuItem and highlight it.
      this.scrollMenuItemIntoView(menuItem.id);
    },
    scrollMenuItemIntoView(menuItemId) {
      this.$nextTick(() => {
        const el = document.querySelector(
          '.menu-item-row[data-menuitemid="' + menuItemId + '"]'
        );
        if (el) {
          el.classList.add('menu-item--temporary-highlight');
          setTimeout(() => {
            el.scrollIntoView({behavior: 'smooth', block: 'center'});
          }, 100);
        }
      });
    },
    clearSearch() {
      this.searchText = '';
    },
    /**
     * Handle menu activation manually to allow for click.stop behaviour.
     *
     * Aka we don't want to trigger the td/row click handler for the
     * button that opens the deactivations menu.
     */
    onDeactivationsMenuActivatorClicked(menuItem) {
      this.$refs['deactivations-menu-' + menuItem.id].isActive =
        !this.$refs['deactivations-menu-' + menuItem.id].isActive;
    },
    goToStoreWithExpandedMenuItems(store) {
      this.$router.push({
        name: 'storemenu',
        params: {storeId: store.id},
        query: {expand_menu_items: true},
      });
    },
    menuCategoryHasDeactivations(menuCategory) {
      const menuItems = this.menuCategoryIdToAugmentedItems[menuCategory.id];
      return menuItems.some((item) => item.has_deactivations);
    },
    toggleMenuItemIsHighlighted(menuItemToHighlight, isHighlight) {
      menuItemToHighlight.is_highlight = isHighlight;
      this.updateMenuItem(menuItemToHighlight);
    },
    confirmToggleMenuItemIsEnabled(menuItem) {
      let confirmed;
      if (menuItem.is_enabled) {
        confirmed = confirm(
          !!this.menu && this.menu.show_menu_item_when_disabled
            ? this.$t('menuItemIsEnabledAlternativeConfirm')
            : this.$t('menuItemIsEnabledConfirm')
        );
      } else {
        confirmed = confirm(this.$t('menuItemIsDisabledConfirm'));
      }
      if (!confirmed) {
        return;
      }

      menuItem.is_enabled = !menuItem.is_enabled;
      this.updateMenuItem(menuItem);
    },
    confirmSortedMenuItems() {
      const orderedMenuItemIds = this.sortableMenuItems.map((item) => item.id);
      this.$api
        .reorderMenuItems(this.selectedMenuCategory.id, orderedMenuItemIds)
        .then(() => {
          this.$bus.$emit('alert-success', {
            message: 'menuItemsReorderedSuccess',
            translate: true,
          });

          this.fetchMenuItems();
        });

      // Frontend update
      this.sortModeEnabled = false;
    },
    updateSortableMenuItems() {
      if (!this.sortModeEnabled) {
        this.sortableMenuItems = [];
        return;
      }
      // Copy current menuItems list so we can resort/modify it.
      // We'll use it to update the actual category order at the end.
      this.sortableMenuItems = this.selectedMenuCategoryMenuItems.slice();
    },
    sortMenuItemsByName() {
      this.sortableMenuItems = orderBy(this.sortableMenuItems, [
        (item) => item.name.toLowerCase(),
      ]);
    },
    handleMenuCategoryPreselection() {
      // a) Preselect based on optional query parameter: ?category=<id>
      if (this.$route.query && this.$route.query.category) {
        for (let i = 0; i < this.orderedMenuCategories.length; i++) {
          const menuCategory = this.orderedMenuCategories[i];
          if (menuCategory.id == this.$route.query.category) {
            this.selectedMenuCategory = menuCategory;
            removeUrlQueryParamsFromCurrentRoute();
            break;
          }
        }
      }
      // b) Preselect based on prior selection, e.g. when navigating back.
      else {
        if (
          this.lastSelectedMenuCategory &&
          this.menuCategories
            .map((category) => category.id)
            .indexOf(this.lastSelectedMenuCategory.id) != -1
        ) {
          this.selectedMenuCategory = this.lastSelectedMenuCategory;
        }
      }
    },
    editMenuItem(menuItem) {
      this.$router.push({
        name: 'editmenuitem',
        params: {menuItemId: menuItem.id},
        query: {menu: this.menuId},
      });
    },
    createMenuCategory(name) {
      this.$api.createMenuCategory(this.menuId, name).then((response) => {
        this.showCreateMenuCategoryDialog = false;

        const newMenuCategory = response.data;
        this.fetchMenuCategories().then(() => {
          this.$nextTick(() => {
            this.selectedMenuCategory = newMenuCategory;
          });
        });

        this.fetchMenuItems();
      });
    },
    updateMenuItem(menuItem) {
      this.busyUpdatingMenuItem = true;
      return this.$api
        .updateMenuItem(menuItem.id, menuItem)
        .catch((error) => {
          console.error(error);
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.fetchMenuItems();
          this.busyUpdatingMenuItem = false;
        });
    },
    openHighlightMenuItemDialog(menuItem) {
      this.menuItemToHighlight = menuItem;
      this.$nextTick(() => {
        this.showHighlightMenuItemDialog = true;
      });
    },
    openDuplicateMenuItemDialog(menuItem) {
      this.menuItemToDuplicate = menuItem;
      this.$nextTick(() => {
        this.showDuplicateMenuItemDialog = true;
      });
    },
    duplicateMenuItem(menuItem) {
      const copiedMenuItem = Object.assign({}, menuItem);
      delete copiedMenuItem.id;

      const duplicateOfMenuItemId = menuItem.id;

      this.$api
        .createMenuItem(this.menuId, copiedMenuItem, duplicateOfMenuItemId)
        .then(() => {
          this.selectedMenuCategory = copiedMenuItem.category;

          const message =
            this.$t('menuItemCopiedSuccess') + ': ' + copiedMenuItem.name;
          this.$bus.$emit('alert-success', {
            message: message,
            translate: false,
          });
          this.fetchMenuItems();
          this.fetchMenuCategories();
          this.fetchMenuAdditives();
        });
    },
    confirmDeleteMenuItem(menuItem) {
      const confirmed = confirm(this.$t('menuItemConfirmDeletion'));
      if (!confirmed) {
        return;
      }
      this.$api.deleteMenuItem(menuItem.id).then(() => {
        this.fetchMenuItems();
        this.fetchMenuCategories();
        this.fetchMenuAdditives();
      });
    },
    fetchData() {
      if (!this.menu) {
        this.fetchMenu({indicator: true});
      }
      this.fetchMenuItems({indicator: true});
      if (
        this.$route.query.category ||
        this.$route.query.edit ||
        !this.menuCategories.length
      ) {
        this.fetchMenuCategories().then(() => {
          this.handleMenuCategoryPreselection();
        });
      }
      if (!this.menuAdditives.length) {
        this.fetchMenuAdditives();
      }

      this.$api
        .warnForMenuTVDecodersWithoutMenuCategories(this.menuId)
        .then((response) => {
          this.hasTVDecodersWithoutMenuCategories = response.data;
        });
    },
    fetchMenu({indicator = false} = {}) {
      if (indicator) {
        this.loadingMenu = true;
      }
      this.$api
        .retrieveMenu(this.menuId)
        .then((response) => {
          this.menu = response.data;
        })
        .finally(() => (this.loadingMenu = false));
    },
    fetchMenuCategories() {
      this.loadingCategories = true;
      return this.$store
        .dispatch('LIST_MENU_CATEGORIES', this.menuId)
        .finally(() => (this.loadingCategories = false));
    },
    fetchMenuAdditives() {
      return this.$store.dispatch('LIST_MENU_ADDITIVES', this.menuId);
    },
    fetchMenuItems({indicator = false, focusMenuItemViaQuery = true} = {}) {
      if (indicator) {
        this.loadingItems = true;
      }
      return this.$store
        .dispatch('LIST_MENU_ITEMS', this.menuId)
        .then(() => {
          this.populateAdditivesTooltipCache();

          // HACK: Make sure the highlighting of newly created items is temporary.
          for (const menuItem of this.menuItems.filter(
            (item) => item.has_just_been_created
          )) {
            setTimeout(function () {
              menuItem.has_just_been_created = false;
            }, 5000);
          }

          if (focusMenuItemViaQuery) {
            const menuItemId = (this.$route.query || {}).item;
            this.scrollMenuItemIntoView(menuItemId);
          }
        })
        .finally(() => {
          this.loadingItems = false;
        });
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.handleMenuCategoryPreselection();
    });
  },
};
</script>

<style scoped>
.v-tabs
  >>> .v-tabs__bar:not(:has(.v-tabs__icon--prev))
  .v-tabs__wrapper--show-arrows {
  margin-left: 0 !important;
}
.v-tabs
  >>> .v-tabs__bar:not(:has(.v-tabs__icon--next))
  .v-tabs__wrapper--show-arrows {
  margin-right: 0 !important;
}

@media (min-width: 600px) {
  .menu-items-table >>> th,
  td {
    padding: 0 12px !important; /* Half horiz. padding for desktop. */
  }
}

.menu-item-row.menu-item--is-disabled {
  background: rgba(255, 0, 0, 0.02);
}
.menu-item-row.menu-item--is-disabled td:not(.buttons-column),
.menu-item-row.menu-item--is-disabled td.buttons-column .match-row-opacity {
  opacity: 0.5;
}

.menu-item--name--line-through {
  text-decoration: line-through solid red;
}

.btn-state--dropdown-icon {
  opacity: 0;
}
.menu-item-state-column:hover .btn-state--dropdown-icon {
  opacity: 1;
}

.menu-item--temporary-highlight {
  animation: menu-item--temporary-highlight-animation 5s ease-in-out;
  animation-iteration-count: 1;
}
@keyframes menu-item--temporary-highlight-animation {
  0% {
    background: rgba(76, 175, 80, 0.16);
  }
  50% {
    background: rgba(76, 175, 80, 0.16);
  }
  100% {
    background: initial;
  }
}

.clickable >>> .v-chip__content:hover {
  cursor: pointer;
}

.v-tab--no-uppercase {
  text-transform: none;
}
</style>



// WEBPACK FOOTER //
// src/pages/MenuItems.vue